import { m, type Message } from '@/i18n';
import { isRouted } from './channels';
import { LEVEL_RANK, type Level, type TagFamily } from './constants';
import type { HealthNode } from './health';

/**
 * Whether the mirrored VACANCY SIGNAL fires (docs/HEALTH_SIGNALS.md §4, §7):
 * the one shipped fact that enters the fold coloured, because it judges a
 * vacancy, not a person. It exists only where someone holds a declared stake -
 * a seated contributor ("nobody wants this") or a dependent ("nobody will make
 * sure this happens"); a requesterless item nobody works on and nothing
 * depends on stays a quiet register fact. The ONE condition, shared by the
 * fact register (facts.ts) and the fold mirror (`deriveTags` below) so they
 * can never disagree. Lives here, not in facts.ts, only to keep the module
 * graph acyclic (facts.ts already imports the decay threshold from here).
 */
export function vacancyFires(inputs: {
  requesterHeld: boolean;
  contributorHeld: boolean;
  dependentCount: number;
}): boolean {
  return !inputs.requesterHeld && (inputs.contributorHeld || inputs.dependentCount > 0);
}

/**
 * Tags: the label primitive for an item's health (docs/CONTRACT_SPINE.md §5.3,
 * the tag system promoted to be the whole health axis). A *derived* tag is read
 * off the item's own state (the pure logic below); a *manual* tag is one a person
 * applies (e.g. `blocked`).
 *
 * The states themselves (key, words, severity level, family) are PLATFORM
 * vocabulary, one constant in this file (`HEALTH_STATES`, ruled 2026-10-14):
 * no org table holds them. Severity is the existing `LEVELS` order
 * (info·notice·warning·critical).
 *
 * After the contract-spine pivot the only **column-derivable** tag is `disputed`
 * (verdict = disputed). `blocked` is now a **manual** impediment tag (§5.3, no
 * longer a phase); the stale/abandoned diagnosis is derived **activity**
 * (§5.4, `diagnoseActivity` below), so neither is derived here.
 */
export const TAGS = [
  'blocked',
  'disputed',
  'stale',
  'unrequested',
  'watch',
  'at_risk',
  'delayed',
  'blocked_upstream',
  'off_track',
] as const;
export type Tag = (typeof TAGS)[number];

// The tag FAMILY (docs/HEALTH_SIGNALS_V2.md §8, review R10): the family decides
// behaviour, and it travels on every resolved `TagDef`, read from the state's
// entry in `HEALTH_STATES` - never re-derived from a key at a consumer. The
// type lives in constants.ts (the single source for every enum). The `rest`
// family (`on_hold`) was retired with the automation-into-flows ruling
// (docs/archive/AUTOMATION_AUDIT.md, 2026-07-22): no machine `on_hold`, no coupling; the
// not-active state is just Idle.
export type { TagFamily } from './constants';

// --- The lifecycle standings -------------------------------------------------
// docs/CLOSED_AXIS.md (three-atom re-cut 2026-07-22; the standing retirement
// the same evening). What the machine STORES about lifecycle is exactly three
// explicit booleans (`delivered`/`active`/`closed`, events remaining truth).
// There is NO phase column and NO platform lifecycle word - the derived
// standing tri-state is RETIRED; laning/grouping speak the attributes
// themselves (domain/lane-attributes.ts), richer vocabulary lives only in a
// flow's authored stage mappings. Done and cancelled are READINGS, never
// state: done = closed AND delivered, cancelled = closed AND NOT delivered.
// `isClosed`/`activeOf` below survive as derived READINGS (display + tense
// inputs), never as a word.

/** The explicit lifecycle standings an item carries (docs/CLOSED_AXIS.md,
 *  three atoms). Each is its own orthogonal atom, written by its own act. */
export interface Standings {
  delivered: boolean;
  active: boolean;
  closed: boolean;
}

/**
 * The lifecycle face "closed" over the standings (docs/CLOSED_AXIS.md, the
 * three-atom re-cut): exactly the `closed` atom - no derived disjunction
 * remains. **`delivered` is deliberately NOT in it**: delivering is the
 * contributor's claim, concluding is `Close`. Kept as a named predicate (not
 * inlined atom checks) because it is the one place the closed reading lives.
 */
export function isClosed(item: Pick<Standings, 'closed'>): boolean {
  return item.closed;
}

/**
 * The lifecycle READING over the atoms (docs/CLOSED_AXIS.md, "Finishing: four
 * positions", ruled by Leon 2026-11-03): the four positions an item can be in,
 * each a reading over two atoms and never stored.
 *
 *   - `open` - not closed, not delivered: the work is under way or not begun;
 *   - `review` - delivered, not closed: the work is handed in and waits on its
 *     review;
 *   - `done` - delivered AND closed: finished with the result standing;
 *   - `cancelled` - closed, NOT delivered: ended without the work being done.
 *
 * ONE function (2026-09-20) so every surface that speaks the reading says the
 * same thing: the state chip's word, the list's state column, the checklist
 * rail's mark, the `done` and `cancelled` search criteria, and the edge table
 * (`domain/lifecycle-edges.ts`), whose positions these are.
 */
export type LifecycleReading = 'open' | 'review' | 'done' | 'cancelled';

export function lifecycleReading(item: Pick<Standings, 'closed' | 'delivered'>): LifecycleReading {
  if (!item.closed) return item.delivered ? 'review' : 'open';
  return item.delivered ? 'done' : 'cancelled';
}

/** The RESULT a stub carries (docs/MODEL.md "Discoverability and
 *  request-access", 2026-10-05): open, done or cancelled, and nothing on how
 *  it goes - so an item in review reads `open` here, as it did before the
 *  review position existed. */
export type ResultReading = Exclude<LifecycleReading, 'review'>;

export function resultReading(item: Pick<Standings, 'closed' | 'delivered'>): ResultReading {
  const reading = lifecycleReading(item);
  return reading === 'review' ? 'open' : reading;
}

/**
 * The derived "in progress" reading of the declared activity flag (§3.3, the "In
 * progress" word): declared active AND not concluded. The `active` DECLARATION
 * persists through a close (orthogonality, docs/CLOSED_AXIS.md - Reopen
 * restores it); this reading is where "closed dominates display" lives - a
 * closed item READS inactive whatever its declared flag says.
 */
export function activeOf(item: Standings): boolean {
  return item.active && !item.closed;
}

/**
 * The contract-state inputs `deriveTags` needs to infer derived (mirrored)
 * tags: the `HealthNode` columns plus the optional STRUCTURE inputs the
 * vacancy mirror reads (docs/HEALTH_SIGNALS.md §4). The structure fields are
 * optional because column-only callers (tests, legacy paths) don't see seats;
 * `resolveItemTags` injects them - absent means "no vacancy statement".
 */
export type TagNode = HealthNode & {
  /** The conclusion atom (docs/CLOSED_AXIS.md): the vacancy mirror gates on
   *  the REAL closure. */
  closed: boolean;
  requesterHeld?: boolean;
  contributorHeld?: boolean;
  dependentCount?: number;
};

/**
 * Derive an item's own MIRRORED tags from its state. Pure. Empty = on track.
 * These are facts wearing catalog keys, never declarations: `disputed` mirrors
 * the human act on the verdict axis; `unrequested` mirrors the seat state (the
 * ONE shipped fact that enters the fold coloured, §4 - it judges a vacancy,
 * not a person, and only where a declared stake exists). Both clear because
 * the world changes (re-accept, seat filled, cancelled in truth); nothing is
 * stored (HS-C1's "nothing may be stored").
 */
export function deriveTags(node: TagNode): Tag[] {
  const tags: Tag[] = [];
  if (node.verdict === 'disputed') tags.push('disputed');
  if (
    node.requesterHeld === false &&
    !isClosed(node) &&
    vacancyFires({
      requesterHeld: false,
      contributorHeld: node.contributorHeld ?? false,
      dependentCount: node.dependentCount ?? 0,
    })
  ) {
    tags.push('unrequested');
  }
  return tags;
}

// --- The activity diagnosis: RETIRED 2026-07-26 (docs/THRESHOLDS.md) ----------
// `STALE_AFTER_DAYS = 14` / `ABANDONED_AFTER_DAYS = 45` and the tempo scale
// that stretched them (`decayThresholds`, BASELINE_BEAT_DAYS, MIN/MAX_TEMPO_
// SCALE) lived here, and `diagnoseActivity` read them. All of it is gone:
//
//   - the numbers were a judgment about when silence starts to matter, authored
//     by nobody and asked of no one (THRESHOLDS §0). They are now DATA on the
//     seeded Lifecycle flow, where they have an author and can be argued with;
//   - the tempo scale silently rescaled them 0.35x to 3x, which would have
//     fired a flow's declared 21 days at 7 - the same defect one level up, and
//     against MACHINE.md §2's own discipline that tempo "states and ranks,
//     never gates or colours". The observed beat stays a ranking input;
//   - `diagnoseActivity` itself was the shipped decay RULE. Rules are the
//     flow's now (`domain/fact-rules.ts`), so it had no jurisdiction left.
//
// `effectiveBeatDays` went with them: the fact path takes the DECLARED cadence
// directly (`domain/cadence.ts`, THRESHOLDS §6's third door) and never mixes an
// observed estimate into a threshold again.

/** A health state resolved for display: a stable key, its words, a severity
 *  level, and the behaviour-deciding family. The words are a `Message`: a
 *  platform state's catalog key (`HEALTH_STATES`), shown in the reader's
 *  locale, or a flow's own word as `verbatim`, shown as its author wrote it. */
export interface TagDef {
  key: string;
  label: Message;
  level: Level;
  family: TagFamily;
}

/** One platform health state: a `TagDef` plus whether the machine applies it
 *  (`derived`: mirrored off the item's state, never declared by a person). */
export interface HealthStateDef extends TagDef {
  key: Tag;
  derived: boolean;
}

/**
 * **The health states are platform vocabulary** (docs/HEALTH_SIGNALS_V2.md §8,
 * ruled by Leon 2026-10-14). The nine shipped states are one constant here,
 * the same in every org and in the commons: no org table holds them, so an
 * org needs no catalog to have health, and a vocabulary change is a code
 * change, not a carry-over migration. Two are computed by the machine by key
 * (`disputed`, `unrequested`); `stale`, `delayed` and `blocked_upstream` are
 * the fixed adopt targets of the quiet, late and dependency facts. The words
 * are catalog keys (`values.health.state.*`), so every surface says them in
 * the reader's locale.
 *
 * A state an organisation wants beyond these is a user tag or a flow stage
 * (docs/FIELDS_AND_TAGS.md), never a new key here. A declaration's explicit
 * grade (the escalation act, HS-J3) and a flow rule's grade override `level`
 * per declaration; `level` is the default.
 *
 * `abandoned` is deliberately NOT a state (review R10, resolved 2026-07-12):
 * the abandoned-quiet diagnosis was computed suggestion vocabulary, never a
 * worn state.
 */
export const HEALTH_STATES: readonly HealthStateDef[] = [
  {
    key: 'disputed',
    label: m('values.health.state.disputed'),
    level: 'critical',
    family: 'mirrored',
    derived: true,
  },
  // `blocked` is a **manual** impediment state (§5.3): a party declares it, and
  // the derivation never reads it off a phase. Ships at WARNING; it escalates
  // to critical only by a human's explicit grade-carrying re-declare or an
  // authored jurisdiction rule, never by a clock (HS-J3).
  {
    key: 'blocked',
    label: m('values.health.state.blocked'),
    level: 'warning',
    family: 'concern',
    derived: false,
  },
  // "Stalled" (2026-07-12): stale is lost motion on work that was underway.
  //
  // DECLARABLE at warning (HEALTH_SIGNALS §4, HS-C6): under the fact model the
  // machine never applies stale - decay is an uncoloured FACT ("quiet 16
  // days") until a human ADOPTS it as this state. The adoption is the "someone
  // cares" that earns the yellow, so the adopted grade defaults to warning.
  {
    key: 'stale',
    label: m('values.health.state.stale'),
    level: 'warning',
    family: 'concern',
    derived: false,
  },
  // The mirrored VACANCY signal (HEALTH_SIGNALS §4, stamped critical): the one
  // shipped fact that enters the fold coloured - it judges a vacancy, not a
  // person. Mirrored at read time by deriveTags (never stored, clears the
  // moment a requester seat is held again).
  {
    key: 'unrequested',
    label: m('values.health.state.unrequested'),
    level: 'critical',
    family: 'mirrored',
    derived: true,
  },
  // The concern vocabulary (HEALTH_SIGNALS_V2.md §8: the RAG tradition plus
  // agile Blocked and GTD Waiting-For, mapped onto the platform grades).
  // Watch is NOTICE-grade: a quiet presence on the item's own surfaces that
  // never takes the dot and never folds (HS-J2, the channel law).
  {
    key: 'watch',
    label: m('values.health.state.watch'),
    level: 'notice',
    family: 'concern',
    derived: false,
  },
  {
    key: 'at_risk',
    label: m('values.health.state.atRisk'),
    level: 'warning',
    family: 'concern',
    derived: false,
  },
  // The late fact's adopt target (every fact is adoptable, HS-G1).
  {
    key: 'delayed',
    label: m('values.health.state.delayed'),
    level: 'warning',
    family: 'concern',
    derived: false,
  },
  // The dependency digest's adopt target: a blocked/late upstream cause a
  // human adopted as their own exposure (HS-H3 - colour lives at the cause
  // until adoption).
  {
    key: 'blocked_upstream',
    label: m('values.health.state.blockedUpstream'),
    level: 'warning',
    family: 'concern',
    derived: false,
  },
  // The one DECLARABLE critical: deliberate human escalation, priced by the
  // channel law (a page, HS-J5), never minted by any clock.
  {
    key: 'off_track',
    label: m('values.health.state.offTrack'),
    level: 'critical',
    family: 'concern',
    derived: false,
  },
];

const HEALTH_STATE_BY_KEY: ReadonlyMap<string, HealthStateDef> = new Map(
  HEALTH_STATES.map((s) => [s.key, s]),
);

/** The platform health state a key names, or undefined for any other key. */
export function healthState(key: string): HealthStateDef | undefined {
  return HEALTH_STATE_BY_KEY.get(key);
}

/** Whether `key` names a platform health state. */
export function isHealthKey(key: string): key is Tag {
  return HEALTH_STATE_BY_KEY.has(key);
}

/** A platform state as a resolved `TagDef`, at the declaration's own grade
 *  when it carries one (the escalation act, HS-J3), else the state's level. */
export function healthDef(key: string, grade: Level | null = null): TagDef | undefined {
  const s = HEALTH_STATE_BY_KEY.get(key);
  if (!s) return undefined;
  return { key: s.key, label: s.label, level: grade ?? s.level, family: s.family };
}

/**
 * The own-health RESTING statement, when no signal is flagged (amended
 * 2026-07-13, docs/HEALTH_SIGNALS.md §6; terminal faces 2026-07-14, v2 §10):
 * the face reads DECLARATIONS - and at settlement the governing declarations
 * are the phase/verdict acts themselves, so the face shifts tense with them.
 *
 * Live work: "On track" ('ok' green only while IN MOTION, the dot law - clean
 * but not-started rests 'idle': green means GOING and clean, and stillness is
 * not second grade). Idle is at rest - there is no separate `on_hold` face
 * anymore (docs/archive/AUTOMATION_AUDIT.md, 2026-07-22); an idle item reads the muted
 * grey "On track", nothing paraphrased.
 *
 * Settled work: NULL - health's face word goes SILENT (v2 §10, MACHINE.md:
 * the phase and verdict words are their own axes' carriers; health saying
 * "Done" would be one axis speaking another's vocabulary). The terminal
 * COLOUR still renders - the glyph reads `healthFace` x `tenseOf`, and
 * health speaks words on settled work only when a declaration is actually
 * worn (a disputed red, a lingering Blocked - the worst-signal side).
 *
 * Pure verdict so the status line, list rows, and cards all speak the same
 * grammar. `level` keys the CSS tokens ('ok' -> healthy, 'idle' -> muted).
 */
export function restingHealth(s: Standings): { label: string; level: 'ok' | 'idle' } | null {
  if (isClosed(s)) return null;
  return { label: 'On track', level: activeOf(s) ? 'ok' : 'idle' };
}

/** The most severe of an item's resolved SIGNAL tags (for a single-chip
 *  summary). Pure verdict over resolved defs, so client-side code (the map's
 *  fold pills) can use it without touching the read layer. */
export function worstTag(defs: TagDef[]): TagDef | null {
  return defs.length
    ? defs.reduce((a, b) => (LEVEL_RANK[b.level] > LEVEL_RANK[a.level] ? b : a))
    : null;
}

/**
 * An item's resolved SIGNAL tags, worst first (critical, warning, notice,
 * info), ties by key so the order is stable. What a summary row renders as
 * its health chips: an item with no signal renders none - clean health is a
 * default, and a default spends no pixel saying so (docs/CLOSED_AXIS.md
 * "Quiet defaults", R1). The dot still carries the resting face.
 */
export function signalsBySeverity<T extends Pick<TagDef, 'key' | 'level'>>(
  defs: readonly T[],
): T[] {
  return [...defs].sort(
    (a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level] || a.key.localeCompare(b.key),
  );
}

/**
 * The DOT verdict (HS-J2, the channel law): the worst signal that reaches the
 * routed surfaces - warning and up. A lone notice-grade signal (Watch) never
 * tints the dot, never enters a fold count, never adds attention weight: it
 * stays a quiet presence on the item's own surfaces (the chip/enumeration
 * side, which keeps using `worstTag`). This is the ONE place the threshold
 * lives; every dot/fold consumer (the Work-list row, the board card's rollup,
 * the map's fold pills, the item health control) applies it through here,
 * never with its own level comparison.
 */
export function dotTag(defs: TagDef[]): TagDef | null {
  const worst = worstTag(defs);
  return worst && isRouted(worst.level) ? worst : null;
}

/** The axes of the item glyph (MACHINE.md §1, the synthesis layer): the mark
 *  is SOLID and says health x tense in COLOUR ALONE. Keyed app-wide as
 *  `data-health` / `data-tense` -> `--status-color` in `globals.css`.
 *  "Finished" is not a channel of the mark - it is attention weight on the
 *  whole row (`data-closed`, see `closedQuiet`). */
export type HealthFace = 'ok' | 'warning' | 'critical';
export type Tense = 'active' | 'idle' | 'concluded';

/**
 * The health half of the glyph (MACHINE.md §1): the worst ROUTED claim in
 * the condition set, or the clean face. Condition ONLY - no motion input,
 * no phase input, no tense input. Notice never registers (HS-J2); rest is
 * valence-free and feeds `tenseOf`, never this.
 */
export function healthFace(defs: TagDef[]): HealthFace {
  const routed = dotTag(defs);
  if (routed) return routed.level === 'critical' ? 'critical' : 'warning';
  return 'ok';
}

/**
 * The tense half of the glyph (MACHINE.md §1): the temporal aspect, three
 * values with a grammar reading - PROGRESSIVE (`active`, being worked), STATIVE
 * (`idle`, sitting: not started, resting, cancelled), PERFECT (`concluded`,
 * arrived: tendered and standing; the verdict stays optional - a dispute flips
 * HEALTH, never this). Its TRUTH is the observed work clock (subtree-scoped,
 * tempo-calibrated - the derived-layer build,
 * docs/archive/TECH_DEBT_RESOLVED.md "Derived-layer builds"); until that ships,
 * and as the shipped default thereafter, the DEFAULT FLOW MAPS it from the
 * phase projection: `done` -> concluded, `active` -> active, everything else ->
 * idle, and a declared rest -> idle even while the phase says active (you
 * stated it is not progressing). A flow may re-map it - mapped as an option,
 * never derived as law.
 *
 * Rendering (globals.css): tense picks the CLEAN face's hue - ok+active and
 * ok+concluded are green (moving, or arrived), ok+idle is plain grey; routed
 * signals stay vivid whatever the tense (the channel law: a blocked item is
 * usually idle, and warnings never whisper). Whether the episode is CLOSED
 * is not a channel of the mark at all - it is row-level attention weight
 * (`closedQuiet` below).
 */
export function tenseOf(s: Standings): Tense {
  // PERFECT = the result stands (a standing tender; a cancelled item -
  // closed without delivery - never reads concluded, it is the muted
  // stillness); PROGRESSIVE = declared active and unconcluded (activeOf -
  // closed dominates). Idle (not active) is the stative rest; there is no
  // separate `on_hold` input anymore (docs/archive/AUTOMATION_AUDIT.md, 2026-07-22).
  if (s.delivered) return 'concluded';
  return activeOf(s) ? 'active' : 'idle';
}

/**
 * Whether a row should RECEDE as closed-and-quiet (variant C, settled
 * 2026-07-20; replaces the ring law that shipped hours earlier). "Finished"
 * is deliberately NOT a channel of the mark: an unfilled circle is the
 * checkbox affordance - hollow-for-done reads as "unfinished, go fill me
 * in", and hollow-for-live would make alarms whisper. So the dot went back
 * to saying health x tense in colour alone, and finished became **attention
 * weight**: the whole closed row/node dims (see `globals.css`,
 * `[data-closed]`), so title, words, dates and avatars recede together.
 *
 * TWO layers are folded into this one predicate, and only one of them is
 * permanent:
 *
 * 1. **The CLOSED mapping** (movable). `closed` means "no further
 *    expectation on anyone" - a MAPPED state that a position PLAYS, not a
 *    phase and not a derivation (docs/REVIEW_AND_ACCEPTANCE.md §4b). The
 *    mapping layer does not exist yet, so today this reads the DERIVED
 *    lifecycle face `isClosed` over the standings (the `closed` atom,
 *    docs/CLOSED_AXIS.md). The review-owed predicate and the mapping layer
 *    both slot in HERE and no rendering changes - the whole reason this is a
 *    named predicate rather than an inline atom check at each call site.
 *    (With review required, a delivery stops concluding and `accepted`
 *    starts: same predicate, new mapping.)
 * 2. **Health outranks closed** (permanent, §4b claim 2). A routed signal
 *    keeps FULL strength whatever the tense - the same law that keeps a
 *    routed dot vivid when idle. A disputed done item is genuinely closed
 *    AND genuinely red: dimming it would re-create the whisper-the-warning
 *    bug the channel law forbids. So a worn routed signal suppresses the
 *    recession, and the mapping above never gets a say.
 */
export function closedQuiet(s: Pick<Standings, 'closed'>, health: HealthFace): boolean {
  if (health !== 'ok') return false;
  return isClosed(s);
}

/** A health state the item wears, as its glyph's tip lists it. */
export interface WornState {
  label: Message;
  level: Level;
}

/**
 * What a glyph's tip lists (2026-10-14): the lifecycle atoms, which the tip
 * speaks through the state-chip law (rest is silent, `stateChips`), and the
 * health states the item wears, worst first. The mark sums them in one
 * colour; the tip names them, on every surface that draws the mark.
 */
export interface GlyphStates {
  standings: Standings;
  worn: WornState[];
}

/** The glyph for a row (MACHINE.md §1): health x tense as the SOLID mark,
 *  plus whether the row recedes as closed, and the states the mark sums. One
 *  object, so a consumer can never stamp half of it. */
export interface Glyph extends GlyphStates {
  health: HealthFace;
  tense: Tense;
  /** Recede this row/node as closed-and-quiet (`closedQuiet`). Not a
   *  property of the MARK - the mark stays solid; this dims its host. */
  closed: boolean;
}

/** No states: an untouched item's, and a summary glyph's, which stands for
 *  several items and so names none of them. */
export const NO_GLYPH_STATES: GlyphStates = {
  standings: { delivered: false, active: false, closed: false },
  worn: [],
};

/**
 * What a glyph's tip says before it lists the states (2026-10-14): the two
 * axes of the mark as two sentences - where the work stands (the lifecycle
 * reading, else the tense), then whether a health state is routed (the hue).
 * An item at rest still gets them, so every dot explains itself.
 */
export function glyphReading(states: GlyphStates): [Message, Message] {
  const s = states.standings;
  const reading = lifecycleReading(s);
  const where =
    reading === 'done'
      ? m('values.glyph.done')
      : reading === 'cancelled'
        ? m('values.glyph.cancelled')
        : reading === 'review'
          ? m('values.glyph.review')
          : activeOf(s)
            ? m('values.glyph.active')
            : m('values.glyph.idle');
  const worst = states.worn.reduce<Level | null>(
    (w, x) => (w === null || LEVEL_RANK[x.level] > LEVEL_RANK[w] ? x.level : w),
    null,
  );
  const health =
    worst === 'critical'
      ? m('values.glyph.critical')
      : worst !== null && isRouted(worst)
        ? m('values.glyph.flagged')
        : m('values.glyph.clean');
  return [where, health];
}

/** The worn health states, worst first, as a glyph's tip lists them. */
export function wornStates(defs: readonly Pick<TagDef, 'label' | 'level'>[]): WornState[] {
  return [...defs]
    .sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level])
    .map((d) => ({ label: d.label, level: d.level }));
}

/**
 * The WHOLE glyph decision in one pure call (MACHINE.md §1): hue from the
 * condition set, tense from the phase projection, and whether the row
 * recedes as closed. Every surface that draws an item dot - list rows, lens
 * lanes, board cards, the item title, the map and minimap nodes - resolves
 * it through here, never with its own comparison.
 */
export function glyphOf(s: Standings, defs: TagDef[]): Glyph {
  const health = healthFace(defs);
  return {
    health,
    tense: tenseOf(s),
    closed: closedQuiet(s, health),
    // The three atoms alone: `s` is often a whole row.
    standings: { delivered: s.delivered, active: s.active, closed: s.closed },
    worn: wornStates(defs),
  };
}

const HEALTH_RANK: Record<HealthFace, number> = { ok: 0, warning: 1, critical: 2 };
const TENSE_RANK: Record<Tense, number> = { concluded: 0, idle: 1, active: 2 };

/**
 * The worst of several glyphs: the one a summary of them shows (the item
 * page's Relationships door row carries the worst glyph among the related
 * items, docs/DESIGN_SYSTEM.md "The item page: no register"). Health decides,
 * critical over warning over clean; among equals, work in motion over idle
 * over concluded, so a clean summary still says whether anything moves. Null
 * for no glyphs.
 */
export function worstGlyph(glyphs: readonly Glyph[]): Glyph | null {
  let worst: Glyph | null = null;
  for (const g of glyphs) {
    if (
      worst === null ||
      HEALTH_RANK[g.health] > HEALTH_RANK[worst.health] ||
      (HEALTH_RANK[g.health] === HEALTH_RANK[worst.health] &&
        TENSE_RANK[g.tense] > TENSE_RANK[worst.tense])
    ) {
      worst = g;
    }
  }
  return worst;
}

/** What the item page's Relationships door row says at rest: the worst
 *  health among its rows - each row's item glyph, where the viewer can open
 *  the item, and the statements that arrived through it - the glyph that
 *  shows it, and how many rows stand at it. A statement relayed from two hops
 *  away raises its row, so a critical cause behind a calm dependency is
 *  visible at rest; it colours the door only, never the item's own dot
 *  (HS-H3). */
export interface RelatedSummary {
  glyph: Glyph | null;
  worst: HealthFace;
  atWorst: number;
}

export function relatedSummary(
  rows: readonly { glyph: Glyph | null; statements: readonly HealthFace[] }[],
): RelatedSummary {
  const faceOf = (row: (typeof rows)[number]) => {
    let face: HealthFace = row.glyph?.health ?? 'ok';
    for (const s of row.statements) if (HEALTH_RANK[s] > HEALTH_RANK[face]) face = s;
    return face;
  };
  const faces = rows.map(faceOf);
  let worst: HealthFace = 'ok';
  for (const face of faces) if (HEALTH_RANK[face] > HEALTH_RANK[worst]) worst = face;
  const shown = worstGlyph(rows.flatMap((row) => (row.glyph ? [row.glyph] : [])));
  const glyph =
    shown === null
      ? worst === 'ok'
        ? null
        : { ...NO_GLYPH_STATES, health: worst, tense: 'idle' as const, closed: false }
      : HEALTH_RANK[worst] > HEALTH_RANK[shown.health]
        ? { ...shown, health: worst, closed: false }
        : shown;
  return {
    glyph,
    worst,
    atWorst: worst === 'ok' ? 0 : faces.filter((face) => face === worst).length,
  };
}

/** Counts of tags by severity level, for a subtree rollup. */
export type SeverityCounts = Record<Level, number>;

export function emptySeverity(): SeverityCounts {
  return { info: 0, notice: 0, warning: 0, critical: 0 };
}

/** Severity counts over a resolved SIGNAL set. Feeds the status line's
 *  truncation summary ("and 1 critical, 2 warnings"): the fold may compress -
 *  only the register must enumerate (v2 §5). */
export function severityCountsOf(defs: readonly TagDef[]): SeverityCounts {
  const counts = emptySeverity();
  for (const d of defs) counts[d.level] += 1;
  return counts;
}
